import ScratchBlocks from 'scratch-blocks';

// ── Undo snapshot ───────────────────────────────────────────────
let _lastSnapshot = null;

/**
 * Save current workspace state before AI modifies it.
 * @returns {boolean} whether snapshot was saved
 */
const saveWorkspaceSnapshot = () => {
    try {
        const workspace = ScratchBlocks.getMainWorkspace();
        if (!workspace) return false;
        const dom = ScratchBlocks.Xml.workspaceToDom(workspace);
        _lastSnapshot = ScratchBlocks.Xml.domToText(dom);
        return true;
    } catch (_e) {
        return false;
    }
};

/**
 * Restore workspace to the state saved before the last AI action.
 * @returns {boolean} whether undo succeeded
 */
const undoLastAiAction = () => {
    try {
        if (!_lastSnapshot) return false;
        const workspace = ScratchBlocks.getMainWorkspace();
        if (!workspace) return false;

        // Clear current blocks
        const topBlocks = workspace.getTopBlocks(false);
        for (let i = 0; i < topBlocks.length; i++) {
            topBlocks[i].dispose(false);
        }

        // Restore snapshot
        const parser = new DOMParser();
        const xmlDoc = parser.parseFromString(_lastSnapshot, 'text/xml');
        ScratchBlocks.Xml.domToWorkspace(xmlDoc.documentElement, workspace);

        _lastSnapshot = null; // consume the snapshot
        return true;
    } catch (_e) {
        return false;
    }
};

/**
 * Check if an undo snapshot is available.
 * @returns {boolean}
 */
const hasUndoSnapshot = () => Boolean(_lastSnapshot);

/**
 * Extract raw XML from AI response text.
 * Handles markdown code fences, extra text, etc.
 * @param {string} text AI response that may contain XML
 * @returns {string|null} clean XML string or null
 */
const extractXML = text => {
    if (!text) return null;

    let cleaned = text.trim();

    // Strip markdown code fences
    const fenceMatch = cleaned.match(
        /```(?:xml)?\s*([\s\S]*?)```/i
    );
    if (fenceMatch) {
        cleaned = fenceMatch[1].trim();
    }

    // Extract <xml>...</xml> from anywhere in the string
    const xmlMatch = cleaned.match(/<xml[\s\S]*?<\/xml>/i);
    if (xmlMatch) {
        return xmlMatch[0];
    }

    // If it starts with a block tag, wrap it
    if (cleaned.match(/^\s*<block/i)) {
        return `<xml>${cleaned}</xml>`;
    }

    return null;
};

/**
 * Remove XML markdown fences and raw XML to get just the conversational text.
 * @param {string} text Full AI response
 * @returns {string} Stripped text for chat bubble
 */
const stripXML = text => {
    if (!text) return '';
    // Remove markdown code fences and their contents
    let stripped = text.replace(/```(?:xml)?\s*[\s\S]*?```/gi, '');
    // Remove raw XML tags if any
    stripped = stripped.replace(/<xml[\s\S]*?<\/xml>/gi, '');
    return stripped.trim();
};

/**
 * Read current workspace blocks as XML string.
 * @returns {string|null} XML string or null if empty
 */
const getWorkspaceXML = () => {
    try {
        const workspace = ScratchBlocks.getMainWorkspace();
        if (!workspace) return null;

        const dom = ScratchBlocks.Xml.workspaceToDom(workspace);
        const xmlText = ScratchBlocks.Xml.domToText(dom);

        // Check if workspace is basically empty
        if (!xmlText || xmlText === '<xml></xml>' ||
            xmlText === '<xml xmlns="https://developers.google.com/blockly/xml"></xml>') {
            return null;
        }

        return xmlText;
    } catch (_e) {
        return null;
    }
};

/**
 * Automatically load any extensions required by the XML string before injection.
 * @param {string} xmlString Raw XML string
 * @param {object} vm The Scratch VM instance
 */
const autoLoadExtensions = async (xmlString, vm) => {
    if (!vm || !xmlString) return;
    const extensionMatch = xmlString.match(/<block type="([a-zA-Z0-9]+)_/g);
    if (extensionMatch) {
        // Extract the prefix (extension ID) from the block types
        const requiredExtensions = new Set(
            extensionMatch.map(m => m.replace('<block type="', '').replace('_', ''))
        );
        
        // List of built-in core Scratch categories that are not extensions
        const coreCategories = [
            'control', 'event', 'motion', 'looks', 'sound', 'sensing', 
            'operators', 'data', 'procedures', 'math', 'text', 'colour', 'argument'
        ];
        
        for (const extId of requiredExtensions) {
            if (!coreCategories.includes(extId) && !vm.extensionManager.isExtensionLoaded(extId)) {
                console.log(`Auto-loading missing extension for AI: ${extId}`);
                try {
                    await vm.extensionManager.loadExtensionURL(extId);
                } catch (e) {
                    console.warn(`Failed to auto-load extension ${extId}:`, e);
                }
            }
        }
    }
};

/**
 * Replace the entire workspace with new XML.
 * @param {string} rawResponse AI response containing XML
 * @param {object} vm Scratch VM instance
 * @returns {boolean} whether replacement succeeded
 */
const replaceWorkspaceXML = async (rawResponse, vm) => {
    try {
        const workspace = ScratchBlocks.getMainWorkspace();
        if (!workspace) return false;

        const xmlString = extractXML(rawResponse);
        if (!xmlString) return false;

        await autoLoadExtensions(xmlString, vm);

        const parser = new DOMParser();
        const xmlDoc = parser.parseFromString(
            xmlString, 'text/xml'
        );

        const errors = xmlDoc.getElementsByTagName('parsererror');
        if (errors.length > 0) return false;

        const xml = xmlDoc.documentElement;

        // Save snapshot before modifying
        saveWorkspaceSnapshot();

        // Safely clear existing blocks so VM catches the delete events
        const topBlocks = workspace.getTopBlocks(false);
        for (let i = 0; i < topBlocks.length; i++) {
            topBlocks[i].dispose(false);
        }
        
        ScratchBlocks.Xml.domToWorkspace(xml, workspace);

        return true;
    } catch (_e) {
        return false;
    }
};

/**
 * Append blocks from XML to the workspace (add mode).
 * @param {string} rawResponse AI response containing XML
 * @param {object} vm Scratch VM instance
 * @returns {boolean} whether injection succeeded
 */
const injectBlocksFromXML = async (rawResponse, vm) => {
    try {
        const workspace = ScratchBlocks.getMainWorkspace();
        if (!workspace) return false;

        const xmlString = extractXML(rawResponse);
        if (!xmlString) return false;

        await autoLoadExtensions(xmlString, vm);

        const parser = new DOMParser();
        const xmlDoc = parser.parseFromString(
            xmlString, 'text/xml'
        );

        const errors = xmlDoc.getElementsByTagName('parsererror');
        if (errors.length > 0) return false;

        const xml = xmlDoc.documentElement;

        // Save snapshot before modifying
        saveWorkspaceSnapshot();

        ScratchBlocks.Xml.appendDomToWorkspace(xml, workspace);

        return true;
    } catch (e) {
        console.error('injectBlocksFromXML error:', e, rawResponse);
        return false;
    }
};

/**
 * Clear all blocks from the workspace.
 * @returns {boolean} whether clearing succeeded
 */
const clearWorkspace = () => {
    try {
        const workspace = ScratchBlocks.getMainWorkspace();
        if (!workspace) return false;
        const topBlocks = workspace.getTopBlocks(false);
        for (let i = 0; i < topBlocks.length; i++) {
            topBlocks[i].dispose(false);
        }
        return true;
    } catch (_e) {
        return false;
    }
};

export {
    extractXML,
    stripXML,
    getWorkspaceXML,
    replaceWorkspaceXML,
    injectBlocksFromXML,
    clearWorkspace,
    saveWorkspaceSnapshot,
    undoLastAiAction,
    hasUndoSnapshot
};
